import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { usePlantContext } from "./plantContext";
import { useUserDetail } from "../services/hooks";
import { useGetOrgBrandingQuery } from "../api/userSlice";
import { useGetPlantThemeQuery } from "../api/vmsSlice";
import { ASSET_LEAVES, leafGet, resolveBranding } from "../services/branding";

const BrandingContext = createContext(null);
// Single freshness policy shared by both paths: RTK Query's own mount/arg-change refetch, and the
// visibility/focus/online listener below — so they never disagree about what counts as "stale".
const STALE_AFTER_SECONDS = 60;
const QUERY_OPTIONS = { refetchOnMountOrArgChange: STALE_AFTER_SECONDS };

function readPersonalMode(userId) {
	if (!userId) return null;
	try {
		const mode = localStorage.getItem(`themeMode:${userId}`);
		return ["lightMode", "darkMode"].includes(mode) ? mode : null;
	} catch { return null; }
}

export function BrandingProvider({ children }) {
	const { userDetail } = useUserDetail();
	const { plantId } = usePlantContext();
	const userId = userDetail?.userId;
	const userOrgId = userDetail?.orgId;
	const isSuperAdmin = userDetail?.userType === "superAdmin";
	const plantEnabled = !!(userId && plantId);
	const plantThemeQuery = useGetPlantThemeQuery(
		{ plantId, userId, orgId: userOrgId }, { ...QUERY_OPTIONS, skip: !plantEnabled }
	);
	// `data` can belong to the previous argument. Never use it for active branding or editing.
	const plantResult = plantEnabled && plantThemeQuery.currentData?.data;
	const plantData = plantResult?.plantId === plantId && (isSuperAdmin || plantResult.orgId === userOrgId) ? plantResult : undefined;
	const orgId = isSuperAdmin ? plantData?.orgId : userOrgId;
	const orgEnabled = !!(userId && orgId);
	const orgBrandingQuery = useGetOrgBrandingQuery({ orgId, userId }, { ...QUERY_OPTIONS, skip: !orgEnabled });
	const orgResult = orgEnabled && orgBrandingQuery.currentData?.data;
	const orgData = orgResult?.orgId === orgId ? orgResult : undefined;
	const plantOverrides = plantData?.overrides || {};
	const orgDefaults = orgData?.themeSettings || {};

	const [modeChoice, setModeChoice] = useState({ userId, mode: readPersonalMode(userId) });
	const personalMode = modeChoice.userId === userId ? modeChoice.mode : readPersonalMode(userId);
	useEffect(() => { setModeChoice({ userId, mode: readPersonalMode(userId) }); }, [userId]);
	const setPersonalMode = useCallback((mode) => {
		if (!userId || !["lightMode", "darkMode"].includes(mode)) return;
		try { localStorage.setItem(`themeMode:${userId}`, mode); } catch { /* Storage is optional. */ }
		setModeChoice({ userId, mode });
	}, [userId]);

	const [, tick] = useState(0);
	// Forces a refetch regardless of freshness — used for explicit "reload now" callers (the
	// Settings "Reload latest" button, tests). The passive focus/online/visibility/interval
	// listener below is not one of those: it should back off once data is already fresh.
	const refresh = useCallback(() => {
		if (plantEnabled && !plantThemeQuery.isFetching) plantThemeQuery.refetch();
		if (orgEnabled && !orgBrandingQuery.isFetching) orgBrandingQuery.refetch();
	}, [plantEnabled, orgEnabled, plantThemeQuery.refetch, orgBrandingQuery.refetch, plantThemeQuery.isFetching, orgBrandingQuery.isFetching]);
	const isFresh = useCallback(() => {
		const freshEnough = (ts) => typeof ts === "number" && Date.now() - ts < STALE_AFTER_SECONDS * 1000;
		return (!plantEnabled || freshEnough(plantThemeQuery.fulfilledTimeStamp)) && (!orgEnabled || freshEnough(orgBrandingQuery.fulfilledTimeStamp));
	}, [plantEnabled, orgEnabled, plantThemeQuery.fulfilledTimeStamp, orgBrandingQuery.fulfilledTimeStamp]);
	// Explicit visibility handling works with the installed RTK version and avoids background polling.
	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState === "hidden") return;
			// Re-render so `now` (and derived expiry checks) recompute even when no refetch is needed.
			tick(n => n + 1);
			if (!isFresh()) refresh();
		};
		const interval = setInterval(onVisible, 300000);
		window.addEventListener("focus", onVisible);
		window.addEventListener("online", onVisible);
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			clearInterval(interval);
			window.removeEventListener("focus", onVisible);
			window.removeEventListener("online", onVisible);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [refresh, isFresh]);
	const now = Date.now();
	const expiries = [plantOverrides, orgDefaults].flatMap(layer => ASSET_LEAVES.map(path => Date.parse(leafGet(layer, path)?.expiresAt))).filter(Number.isFinite);
	const nextExpiry = Math.min(...expiries.filter(expiry => expiry > now));
	useEffect(() => {
		if (!Number.isFinite(nextExpiry)) return;
		const timer = setTimeout(() => { tick(n => n + 1); if (document.visibilityState !== "hidden") refresh(); }, Math.max(0, nextExpiry - Date.now()) + 1);
		return () => clearTimeout(timer);
	}, [nextExpiry, refresh]);

	useEffect(() => {
		const expired = layer => ASSET_LEAVES.some(path => Date.parse(leafGet(layer, path)?.expiresAt) <= Date.now());
		if (plantData && expired(plantData.overrides)) plantThemeQuery.refetch();
		if (orgData && expired(orgData.themeSettings)) orgBrandingQuery.refetch();
	}, [plantData, orgData, plantThemeQuery.refetch, orgBrandingQuery.refetch]);
	// superAdmin has no "home" org — `plantId` (services/context/plantContext.jsx) can point at any
	// tenant's plant (an arbitrary first option, or a stale localStorage selection from a previous
	// session). Never let the app's own chrome (navbar color, favicon, title, dashboard stat icons —
	// every consumer of `effective`) adopt that tenant's branding. Only the Settings theme editor
	// needs per-tenant branding, and it reads `orgDefaults`/`plantOverrides` directly, not `effective`.
	const effective = isSuperAdmin ? resolveBranding({}, {}, now) : resolveBranding(plantOverrides, orgDefaults, now);
	const configuredMode = effective.theme.selectedTheme.value;
	const isError = plantThemeQuery.isError || orgBrandingQuery.isError;
	const value = {
		effective, plantOverrides, orgDefaults, revision: plantData?.revision ?? 0,
		plantId, orgId, userId, configuredMode, renderedMode: personalMode || configuredMode,
		personalMode, setPersonalMode, refresh, plantThemeQuery, orgBrandingQuery,
		isReady: !!(plantData && orgData && plantThemeQuery.isSuccess && orgBrandingQuery.isSuccess && !isError),
		isLoading: (plantEnabled && !plantData && !plantThemeQuery.isError) || (orgEnabled && !orgData && !orgBrandingQuery.isError),
		isFetching: plantThemeQuery.isFetching || orgBrandingQuery.isFetching,
		isError,
		assetsExpired: expiries.some(expiry => expiry <= now),
	};
	return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}
export function useBranding() {
	const context = useContext(BrandingContext);
	if (!context) throw new Error("useBranding must be used within a BrandingProvider");
	return context;
}
